import { z } from 'zod'
import { AsyncFn, LocalDateString } from '../../common'
import {
  guidSchema,
  MaintenancePlanPaymentInterval,
  MaintenancePlanVisitConfig,
  maintenancePlanVisitConfigSchema,
} from '../../contracts'
import { CompanyGuidContainer, ForCompany, ForCompanyUser } from '../Company/Company'
import { bzOptional, Guid } from '../common-schemas'
import { EquipmentType } from '../Equipment/EquipmentType'
import { CartItem } from '../Finance/Transactions/TransactionTypes'
import { JobClass } from '../Job'
import { PricebookTaxRateDto } from '../Pricebook/PricebookTypes'
import { EquipmentCoverageDefinition } from './EquipmentCoverageTypes'

export type MaintenancePlanDefinitionVisitConfig = MaintenancePlanVisitConfig

export enum MaintenancePlanVisitReplenishmentStrategy {
  PURCHASE_ANNIVERSARY = 'PURCHASE_ANNIVERSARY',
  FIXED_DATE = 'FIXED_DATE',
}

export type MaintenancePlanVisitReplenishmentConfig = {
  readonly strategy: MaintenancePlanVisitReplenishmentStrategy
  readonly month: number
  readonly day: number
}

export const maintenancePlanVisitReplenishmentConfigSchema = z.object({
  strategy: z.nativeEnum(MaintenancePlanVisitReplenishmentStrategy),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(28),
})

export const defaultMaintenancePlanVisitReplenishmentConfig: MaintenancePlanVisitReplenishmentConfig = {
  strategy: MaintenancePlanVisitReplenishmentStrategy.PURCHASE_ANNIVERSARY,
  month: 1,
  day: 1,
}

const toVisitReplenishmentStrategy = (
  strategy?: MaintenancePlanVisitReplenishmentStrategy | string | null,
): MaintenancePlanVisitReplenishmentStrategy => {
  switch (strategy) {
    case MaintenancePlanVisitReplenishmentStrategy.FIXED_DATE:
      return MaintenancePlanVisitReplenishmentStrategy.FIXED_DATE
    case MaintenancePlanVisitReplenishmentStrategy.PURCHASE_ANNIVERSARY:
    default:
      return MaintenancePlanVisitReplenishmentStrategy.PURCHASE_ANNIVERSARY
  }
}

export const toVisitReplenishmentAnchorOverrideLocalDate = ({
  month,
  day,
}: {
  month: number
  day: number
}): LocalDateString => `2000-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}` as LocalDateString

const maintenancePlanBenefitSchema = z.object({
  maintenancePlanDefinitionBenefitGuid: bzOptional(guidSchema),
  name: z.string(),
  description: z.string(),
})

const maintenancePlanDiscountSchema = z.object({
  maintenancePlanDefinitionDiscountGuid: guidSchema,
  discountJobClass: z.nativeEnum(JobClass),
  discountRate: z.number(),
})

export const maintenancePlanFlareSchema = z.object({
  imageUrl: z.string(),
  primaryColorHex: z.string(),
})

export const maintenancePlanAllowedPaymentIntervalSchema = z.object({
  maintenancePlanDefinitionAllowedPaymentIntervalGuid: guidSchema,
  paymentInterval: z.nativeEnum(MaintenancePlanPaymentInterval),
  isDefault: z.boolean(),
})

export const maintenancePlanDefinitionSchema = z.object({
  maintenancePlanDefinitionGuid: guidSchema,
  companyGuid: guidSchema,
  numVisitCreditsPerYear: z.number(),
  yearlyStaticPriceUsc: bzOptional(z.number()),
  numDaysUntilVisitCreditExpiration: z.number(),
  name: z.string(),
  shortDescription: z.string(),
  termsAndConditions: z.string(),
  isAvailableForSale: z.boolean(),
  flare: bzOptional(maintenancePlanFlareSchema),
  benefits: z.array(maintenancePlanBenefitSchema),
  discounts: z.array(maintenancePlanDiscountSchema),
  allowedPaymentIntervals: z.array(maintenancePlanAllowedPaymentIntervalSchema),
  visitConfigs: bzOptional(z.array(maintenancePlanVisitConfigSchema)),
  defaultBusinessUnitGuid: bzOptional(guidSchema),
})

export type ComprehensiveMaintenancePlanConfigViewModel = {
  readonly maintenancePlansEnabled: boolean
  readonly maintenancePlanSavingsEnabled: boolean
  readonly maintenancePlanSavingsCtaUrl?: string | null
  readonly visitReplenishmentConfig: MaintenancePlanVisitReplenishmentConfig
  readonly plans: ReadonlyArray<MaintenancePlanDefinition>
  readonly equipmentCoverageDefinitions: ReadonlyArray<EquipmentCoverageDefinition>
  readonly taxRate?: PricebookTaxRateDto
  readonly serviceItem?: CartItem
  readonly visitConfigs?: ReadonlyArray<MaintenancePlanDefinitionVisitConfig>
}

export type ComprehensiveMaintenancePlanConfigReader = AsyncFn<
  CompanyGuidContainer,
  ComprehensiveMaintenancePlanConfigViewModel
>

export type MaintenancePlanVisitReplenishmentConfigReader = AsyncFn<
  CompanyGuidContainer,
  MaintenancePlanVisitReplenishmentConfig
>

export type MaintenancePlanVisitReplenishmentConfigWriter = AsyncFn<
  ForCompanyUser<MaintenancePlanVisitReplenishmentConfig>
>

export type MaintenancePlanSavingsConfigUpdate = {
  readonly maintenancePlanSavingsEnabled: boolean
  readonly maintenancePlanSavingsCtaUrl?: string | null
}

export type MaintenancePlanDefinitionReader = AsyncFn<
  ForCompany<MaintenancePlanDefinitionGuidContainer>,
  MaintenancePlanDefinition
>

export type MaintenancePlanDefinitionGuid = Guid
export type MaintenancePlanDefinitionGuidContainer = {
  readonly maintenancePlanDefinitionGuid: MaintenancePlanDefinitionGuid
}

export type MaintenancePlanDefinition = MaintenancePlanBaseDefinition &
  MaintenancePlanDefinitionMarketingInfo & {
    readonly flare?: MaintenancePlanDefinitionFlare
    readonly benefits: MaintenancePlanDefinitionBenefit[]
    readonly discounts: MaintenancePlanDefinitionDiscount[]
    readonly allowedPaymentIntervals: MaintenancePlanDefinitionAllowedPaymentInterval[]
    readonly visitConfigs?: MaintenancePlanDefinitionVisitConfig[]
    readonly defaultBusinessUnitGuid?: string
  }

export type MaintenancePlanBaseDefinition = MaintenancePlanDefinitionGuidContainer & {
  readonly companyGuid: string
  readonly numVisitCreditsPerYear: number
  readonly numDaysUntilVisitCreditExpiration: number
  readonly yearlyStaticPriceUsc?: number | null
}

export type MaintenancePlanDefinitionFlare = {
  readonly imageUrl: string
  readonly primaryColorHex: string
}

export type MaintenancePlanDefinitionMarketingInfo = {
  readonly name: string
  readonly shortDescription: string
  readonly termsAndConditions: string
  readonly isAvailableForSale: boolean
}

export type MaintenancePlanDefinitionBenefit = {
  readonly maintenancePlanDefinitionBenefitGuid?: Guid
  readonly name: string
  readonly description: string
}

export type MaintenancePlanDefinitionDiscount = {
  readonly maintenancePlanDefinitionDiscountGuid: Guid
  readonly discountJobClass: JobClass
  readonly discountRate: number
}

export type MaintenancePlanDefinitionAllowedPaymentInterval = {
  readonly maintenancePlanDefinitionAllowedPaymentIntervalGuid: Guid
  readonly paymentInterval: MaintenancePlanPaymentInterval
  readonly isDefault: boolean
}

export type MaintenancePlanRenewalType = 'AUTO_RENEW' | 'MANUAL_RENEW'
export const MAINTENANCE_PLAN_MANUAL_RENEWAL_NUMBER_OF_DAYS_TIL_EXPIRATION = 365

export const getCoveredEquipmentTypesMap = (
  items?: readonly EquipmentCoverageDefinition[],
): Map<EquipmentType, EquipmentCoverageDefinition> =>
  (items ?? []).reduce((acc, e) => {
    acc.set(e.equipmentType, e)
    return acc
  }, new Map<EquipmentType, EquipmentCoverageDefinition>())

export type RenewalTypeOption = 'AUTO' | 'MANUAL' | 'BOTH'

export const getRenewalTypeOptionFromBillingProfiles = (
  billingProfiles: { maintenancePlanAutoRenewalAllowed: boolean; maintenancePlanManualRenewalAllowed: boolean }[],
) => {
  if (billingProfiles.length < 1) return 'AUTO'
  const bp = billingProfiles[0]
  if (bp.maintenancePlanAutoRenewalAllowed && bp.maintenancePlanManualRenewalAllowed) return 'BOTH'
  if (bp.maintenancePlanManualRenewalAllowed) return 'MANUAL'

  return 'AUTO'
}

export const getFixedPlanDurationsFromBillingProfiles = (
  billingProfiles: { maintenancePlanStaticDurationNumYearsAllowed: number[] }[],
): number[] => {
  if (billingProfiles.length < 1) return [1]

  const bp = billingProfiles[0]
  return bp.maintenancePlanStaticDurationNumYearsAllowed
}

export const getVisitReplenishmentConfigFromBillingProfiles = (
  billingProfiles: {
    visitReplenishmentStrategy?: MaintenancePlanVisitReplenishmentStrategy | string | null
    visitReplenishmentMonth?: number | null
    visitReplenishmentDay?: number | null
  }[],
): MaintenancePlanVisitReplenishmentConfig => {
  if (billingProfiles.length < 1) return defaultMaintenancePlanVisitReplenishmentConfig

  const bp = billingProfiles[0]
  return {
    strategy: toVisitReplenishmentStrategy(bp.visitReplenishmentStrategy),
    month: bp.visitReplenishmentMonth ?? defaultMaintenancePlanVisitReplenishmentConfig.month,
    day: bp.visitReplenishmentDay ?? defaultMaintenancePlanVisitReplenishmentConfig.day,
  }
}
